import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Quote, ArrowRight, Award } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { SectionHeader } from "@/components/ui-kit";
import { TEAM } from "@/lib/team";
import { MEDIA } from "@/lib/media";

export default function GoldenFounder() {
  useSeo("Golden Founder", "Meet the founders and team behind Golden Wolf Foundation — Toriano Sanzone, Rozanna Rivera, Greg Richard and the trainers serving veterans in North Lawndale, Chicago.");
  const founder = TEAM[0];
  const others = TEAM.slice(1);

  return (
    <div>
      {/* Founder hero */}
      <section className="relative grain border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline mb-4 flex items-center gap-2"><Award className="w-4 h-4" /> The Golden Founder</div>
            <h1 className="font-heading text-4xl sm:text-6xl font-bold text-[#FDFBF7] leading-tight">{founder.name}</h1>
            <p className="text-xl text-[#D4AF37] mt-2 font-heading italic">{founder.role}</p>
            <p className="mt-6 text-lg text-[#C8C4B7] leading-relaxed">{founder.lead}</p>
            <div className="mt-6 space-y-4">
              {founder.bio.map((p, i) => <p key={i} className="text-[#C8C4B7] leading-relaxed">{p}</p>)}
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {founder.tags.map((t) => <span key={t} className="text-xs uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1">{t}</span>)}
            </div>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Link to="/donate" className="btn-gold" data-testid="founder-donate">Support the Mission</Link>
              <Link to="/mission" className="btn-outline" data-testid="founder-mission">Our Mission</Link>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative">
            <div className="relative gw-card overflow-hidden">
              <img src={founder.image} alt={founder.name} className="w-full h-[520px] object-cover object-top" data-testid="founder-photo" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 flex items-center gap-3">
                <img src={MEDIA.logo} alt="Golden Wolf" className="w-12 h-12 object-contain" />
                <div><div className="text-[#FDFBF7] font-heading text-lg">{founder.name}</div><div className="text-xs text-[#D4AF37]">{founder.role}</div></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quote band */}
      <section className="bg-[#141414] border-b border-white/10 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Quote className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
          <p className="font-heading text-2xl sm:text-3xl text-[#FDFBF7] leading-snug">"You Can't Burn Without Fire."</p>
          <p className="mt-4 text-[#8A877B]">Every veteran carries a flame that never went out. Golden Wolf gives it direction.</p>
        </div>
      </section>

      {/* Team */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <SectionHeader overline="Golden Wolf Team" title="The People Behind the Pack" sub="Founders, trainers and veteran advocates building a national model of service in North Lawndale, Chicago." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {others.map((m, i) => (
            <motion.div key={m.name} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
              className="gw-card overflow-hidden flex flex-col" data-testid={`team-${m.name.replace(/\s/g, "-").toLowerCase()}`}>
              <div className="relative h-72"><img src={m.image} alt={m.name} className="w-full h-full object-cover object-top" /><div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 to-transparent" /></div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-heading text-xl font-semibold text-[#FDFBF7]">{m.name}</h3>
                <p className="text-sm text-[#D4AF37] mt-0.5">{m.role}</p>
                <p className="text-sm text-[#C8C4B7] mt-3 flex-1">{m.bio[0]}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {m.tags.map((t) => <span key={t} className="text-[10px] uppercase tracking-wider text-[#8A877B] border border-white/15 px-2 py-0.5">{t}</span>)}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link to="/volunteer" className="btn-outline inline-flex items-center gap-2" data-testid="founder-join">Join the Team <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </div>
  );
}
